import React, { useMemo } from 'react';

import { Route, createRoutesFromElements } from 'react-router-dom';

import { TabTitle } from '@adsk/exo-skeleton-react';

import { getEnvConfig } from '../../envs/envConfig';
import {
  LAYOUTS,
  ProjectsRoute,
  PublicRoute,
  ToolRoute,
} from '../../envs/types';
import Projects from '../../routes/Projects/Projects';
import ProjectsLoader from '../../routes/Projects/ProjectsLoader';
import AppContentError from '../AppContentError/AppContentError';
import ToolContentError from '../AppContentError/ToolContentError';
import ExoSkeletonApi from '../ExoSkeletonApi/ExoSkeletonApi';
import PageNotFoundExo from '../PageNotFoundExo/PageNotFoundExo';
import Redirect from '../Redirect/Redirect';
import RemoteComponent from '../RemoteComponent/RemoteComponent';
import { RootErrorBoundary } from '../RootErrorBoundary';

const RootProjectPageLayout = React.lazy(
  () => import('../RootProjectPageLayout'),
);

const RootPublicPageLayout = React.lazy(
  () => import('../RootPublicPageLayout'),
);

const redirects = [
  {
    from: '/build/projects/project-templates',
    to: '/projects/templates',
  },
  {
    from: '/projects/project-templates',
    to: '/projects/templates',
  },
  {
    from: '/build/projects',
    to: '/projects',
  },
  {
    from: '/projects/projects',
    to: '/projects',
  },
  /* These redirects have been showing on our infra, moving it here to collect more information about these requests */
  {
    from: '/login',
    to: '/projects',
  },
  {
    from: '/build/',
    to: '/projects',
  },
];

const useLayoutRoutes = () => {
  const { remoteRoutes } = useMemo(() => getEnvConfig(), []);

  return useMemo(() => {
    const projectPageLayoutRoutes = remoteRoutes.filter(
      (route): route is ToolRoute =>
        route.layout === LAYOUTS.PROJECT_PAGE_LAYOUT,
    );

    const projectDirectoryLayoutRoutes = remoteRoutes.filter(
      (route): route is ProjectsRoute =>
        route.layout === LAYOUTS.PROJECT_DIRECTORY_LAYOUT,
    );

    const publicPageLayoutRoutes = remoteRoutes.filter(
      (route): route is PublicRoute =>
        route.layout === LAYOUTS.PUBLIC_PAGE_LAYOUT,
    );

    return {
      projectPageLayoutRoutes,
      projectDirectoryLayoutRoutes,
      publicPageLayoutRoutes,
    };
  }, [remoteRoutes]);
};

export const useRoutes = () => {
  const {
    projectPageLayoutRoutes,
    projectDirectoryLayoutRoutes,
    publicPageLayoutRoutes,
  } = useLayoutRoutes();

  const routesObject = useMemo(
    () =>
      createRoutesFromElements(
        <Route element={<RootErrorBoundary />}>
          <Route element={<ExoSkeletonApi />}>
            <Route path="/" element={<RootProjectPageLayout />}>
              {projectPageLayoutRoutes.map(
                ({
                  path,
                  scope,
                  modulePath,
                  customTitle,
                  layoutProps: { currentToolId },
                }) => {
                  return (
                    <Route
                      key={path}
                      path={path}
                      element={
                        <>
                          {customTitle ?? <TabTitle />}
                          <RemoteComponent
                            scope={scope}
                            module={modulePath}
                            toolId={currentToolId}
                            renderError={() =>
                              currentToolId ? (
                                <ToolContentError />
                              ) : (
                                <AppContentError />
                              )
                            }
                          />
                        </>
                      }
                    />
                  );
                },
              )}
            </Route>
            <Route element={<Projects />}>
              {projectDirectoryLayoutRoutes.map(
                ({ path, scope, modulePath }) => {
                  return (
                    <Route
                      key={path}
                      path={path}
                      element={
                        <RemoteComponent
                          scope={scope}
                          module={modulePath}
                          renderLoader={() => <ProjectsLoader />}
                        />
                      }
                    />
                  );
                },
              )}
            </Route>
            <Route element={<RootPublicPageLayout />}>
              {publicPageLayoutRoutes.map(
                ({
                  path,
                  scope,
                  modulePath,
                  customTitle,
                  layoutProps: { currentProductId },
                }) => {
                  return (
                    <Route
                      key={path}
                      path={path}
                      element={
                        <>
                          {customTitle ?? (
                            <TabTitle productId={currentProductId} />
                          )}
                          <RemoteComponent scope={scope} module={modulePath} />
                        </>
                      }
                    />
                  );
                },
              )}
            </Route>
            {redirects.map(({ from, to }) => (
              <Route
                key={from}
                path={from}
                element={<Redirect from={from} to={to} />}
              />
            ))}
            <Route path="*" element={<PageNotFoundExo />} />
          </Route>
        </Route>,
      ),
    [
      projectDirectoryLayoutRoutes,
      projectPageLayoutRoutes,
      publicPageLayoutRoutes,
    ],
  );

  return {
    routesObject,
  };
};
